import { useEffect, useRef, useState } from 'react';
import { db, now, uid } from '../db/dexie';
import type { Attachment } from '../db/types';
import { isImage, processImage } from '../lib/image';
import { sortKeyBetween } from '../db/dexie';
import './AttachmentGallery.css';

interface Props {
  itemId: string;
  attachments: Attachment[];
  onOpen: (index: number) => void;
}

/** Thumbnail strip: add, reorder, delete, open. */
export function AttachmentGallery({ itemId, attachments, onOpen }: Props) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  async function add(files: FileList | File[]) {
    const images = [...files].filter(isImage);
    let sort = (attachments.at(-1)?.sort_order ?? 0) + 1;
    for (const f of images) {
      const { blob, width, height, mime } = await processImage(f);
      const id = uid();
      await db.transaction('rw', db.attachments, db.blobs, async () => {
        await db.attachments.add({
          id,
          item_id: itemId,
          storage_path: `local/${id}.webp`,
          mime_type: mime,
          width,
          height,
          bytes: blob.size,
          sort_order: sort++,
          // Blob first, metadata second, upload third. The row exists locally
          // and is usable long before the network hears about it.
          uploaded: 0,
          deleted: false,
          dirty: 1,
          created_at: now(),
          updated_at: now(),
        });
        await db.blobs.add({ attachment_id: id, data: blob });
      });
    }
  }

  async function drop(targetId: string) {
    if (!dragging || dragging === targetId) return;
    const order = attachments.filter((a) => a.id !== dragging);
    const at = order.findIndex((a) => a.id === targetId);
    const sort_order = sortKeyBetween(order[at - 1]?.sort_order, order[at]?.sort_order);
    await db.attachments.update(dragging, { sort_order, updated_at: now(), dirty: 1 });
    setDragging(null);
  }

  return (
    <div
      className={`gallery${over ? ' is-over' : ''}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!e.dataTransfer.files.length) return;
        e.preventDefault();
        setOver(false);
        void add(e.dataTransfer.files);
      }}
    >
      <span className="gallery__label">
        {attachments.length} {attachments.length === 1 ? 'IMAGE' : 'IMAGES'}
      </span>

      {attachments.map((a, i) => (
        <button
          key={a.id}
          type="button"
          className={`gallery__thumb${dragging === a.id ? ' is-dragging' : ''}`}
          draggable
          onDragStart={() => setDragging(a.id)}
          onDragEnd={() => setDragging(null)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => void drop(a.id)}
          onClick={() => onOpen(i)}
          title={a.storage_path}
        >
          <Thumb attachment={a} />
          <span className="gallery__name">{basename(a.storage_path)}</span>
        </button>
      ))}

      <button type="button" className="gallery__drop" onClick={() => file.current?.click()}>
        Drop image
      </button>
      <input
        ref={file}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) void add(e.target.files);
          e.target.value = '';
        }}
      />

      <span className="spacer" />
      <span className="gallery__hint">Click to open · drag to reorder</span>
    </div>
  );
}

/**
 * Resolves an attachment to a local object URL. A row with no blob yet is the
 * normal state between metadata pull and blob download — it renders as a
 * placeholder rather than a broken image.
 */
export function useBlobURL(attachment: Attachment | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked = false;
    let objectURL: string | null = null;
    setUrl(null);
    if (!attachment) return;
    void db.blobs.get(attachment.id).then((row) => {
      if (!row || revoked) return;
      objectURL = URL.createObjectURL(row.data);
      setUrl(objectURL);
    });
    return () => {
      revoked = true;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [attachment]);
  return url;
}

function Thumb({ attachment }: { attachment: Attachment }) {
  const url = useBlobURL(attachment);
  if (!url) return <span className="gallery__image hatch" />;
  return <img className="gallery__image" src={url} alt="" draggable={false} />;
}

export const basename = (path: string) => path.slice(path.lastIndexOf('/') + 1).replace(/\.\w+$/, '');
